import { useState, useEffect, useCallback } from 'react';
import { AnimatedBlobs } from './AnimatedBlobs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faChevronLeft,
  faChevronRight,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

export interface SidebarAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  badge?: string | number;
  gradient?: string;
  section: 'views' | 'platforms' | 'tools' | 'system';
}

interface AppSidebarProps {
  actions: SidebarAction[];
  currentView?: string;
  onCollapsedChange?: (collapsed: boolean) => void;
  onMobileChange?: (isMobile: boolean) => void;
  showRainbow?: boolean;
}

const SECTION_LABELS: Record<string, string> = {
  views: 'Views',
  platforms: 'Platform',
  tools: 'Tools',
  system: 'System',
};

const MOBILE_BREAKPOINT = 768;

export function AppSidebar({ actions, currentView, onCollapsedChange, onMobileChange, showRainbow = true }: AppSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Detect mobile
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      onMobileChange?.(mobile);
      if (mobile) {
        setCollapsed(false);
        onCollapsedChange?.(false);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [onMobileChange, onCollapsedChange]);

  const handleCollapse = (val: boolean) => {
    setCollapsed(val);
    onCollapsedChange?.(val);
  };

  const handleMobileAction = useCallback((action: SidebarAction) => {
    action.onClick();
    setMobileOpen(false);
  }, []);

  const sections = ['views', 'platforms', 'tools', 'system'] as const;

  // Sidebar content (shared between desktop and mobile)
  const sidebarContent = (showLabels: boolean) => (
    <>
      {/* Logo / Brand */}
      <div className={`app-sidebar-brand flex items-center gap-3 px-4 pt-6 pb-4 ${!showLabels ? 'justify-center' : ''}`}>
        <img src="/verigit.png" alt="VeriGit" className="h-9 object-contain flex-shrink-0 rounded-xl" />
        {showLabels && (
          <div className="overflow-hidden flex-1">
            <h1 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">VeriGit</h1>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate leading-tight">Git Analytics</p>
          </div>
        )}
        {isMobile && showLabels && (
          <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-5">
        {sections.map((section) => {
          const sectionActions = actions.filter((a) => a.section === section);
          if (sectionActions.length === 0) return null;

          return (
            <div key={section}>
              {showLabels && (
                <p className="px-2 mb-2 text-[11px] font-medium text-gray-400 dark:text-gray-500 tracking-wide">
                  {SECTION_LABELS[section]}
                </p>
              )}
              {!showLabels && <div className="mb-1 mx-auto w-6 border-t border-gray-200 dark:border-gray-800" />}
              <div className="space-y-0.5">
                {sectionActions.map((action) => {
                  const isActive = currentView === action.id || action.active;
                  return (
                    <button
                      key={action.id}
                      onClick={() => isMobile ? handleMobileAction(action) : action.onClick()}
                      disabled={action.disabled}
                      title={!showLabels ? action.label : undefined}
                      className={`
                        group relative w-full flex items-center gap-3 rounded-xl text-[13px] font-medium transition-all duration-150
                        ${!showLabels ? 'justify-center px-0 py-2.5' : 'px-3 py-2'}
                        ${isActive
                          ? 'bg-gray-900/[0.06] dark:bg-white/[0.08] text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-900/[0.04] dark:hover:bg-white/[0.04]'
                        }
                        ${action.disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <span className={`flex-shrink-0 transition-colors duration-150 ${
                        isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                      }`}>
                        {action.icon}
                      </span>
                      {showLabels && (
                        <>
                          <span className="truncate">{action.label}</span>
                          {action.badge != null && (
                            <span className="ml-auto flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                              {action.badge}
                            </span>
                          )}
                        </>
                      )}
                      {!showLabels && action.badge != null && (
                        <span className="absolute -top-0.5 -right-0.5 px-1 py-0.5 text-[9px] font-bold rounded-full bg-blue-500 text-white min-w-[16px] text-center shadow-sm">
                          {action.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle — desktop only */}
      {!isMobile && (
        <div className="px-3 py-3 border-t border-black/[0.04] dark:border-white/[0.04]">
          <button
            onClick={() => handleCollapse(!collapsed)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-900/[0.04] dark:hover:bg-white/[0.04] transition-all duration-150 text-[13px] ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            {collapsed ? <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" /> : <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      )}
    </>
  );

  // Mobile: overlay sidebar + backdrop
  if (isMobile) {
    return (
      <>
        {/* Mobile hamburger button */}
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-3 left-3 z-50 p-2.5 rounded-xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border border-black/[0.06] dark:border-white/[0.06] shadow-sm hover:shadow-md transition-all"
          style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
        >
          <FontAwesomeIcon icon={faBars} className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>

        {/* Backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Slide-out panel */}
        <aside
          className={`fixed left-0 top-0 h-screen z-50 w-[260px] flex flex-col transition-transform duration-300 ease-in-out ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
        >
          {/* Rainbow blobs */}
          {showRainbow && <AnimatedBlobs scale={0.7} />}
          <div className={`absolute inset-0 ${showRainbow ? 'bg-white/50 dark:bg-slate-950/60' : 'bg-white/70 dark:bg-slate-950/70'} backdrop-blur-xl border-r border-black/[0.06] dark:border-white/[0.06]`} />
          <div className="relative flex flex-col h-full">
            {sidebarContent(true)}
          </div>
        </aside>
      </>
    );
  }

  // Desktop: fixed sidebar
  return (
    <aside
      className={`fixed left-0 top-0 h-screen z-40 flex flex-col transition-all duration-300 ease-in-out ${
        collapsed ? 'w-[68px]' : 'w-[220px]'
      }`}
      style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
    >
      {/* Rainbow blobs */}
      {showRainbow && <AnimatedBlobs scale={0.7} />}
      <div className={`absolute inset-0 ${showRainbow ? 'bg-white/50 dark:bg-slate-950/60' : 'bg-white/70 dark:bg-slate-950/70'} backdrop-blur-xl border-r border-black/[0.06] dark:border-white/[0.06]`} />
      <div className="relative flex flex-col h-full">
        {sidebarContent(!collapsed)}
      </div>
    </aside>
  );
}

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 68;

